import { Box, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, MenuItem, Select, InputLabel, FormControl, Pagination, Chip, Grid, Divider, Button, Checkbox, ListItemText } from "@mui/material";
import { PaymentServices } from "../../services/AllServices";
import React, { useEffect, useState, useMemo, useRef } from "react";
import { Formik } from "formik";
import { IconButton, Dialog, DialogTitle, DialogContent, Typography } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { showDateTime } from "../../utils/formatTime";

const statusOptions = ["Pending", "Completed", "Failed", "Transfer Complete"];
const statusOptionsChange = ["Completed", "Transfer Complete"];
const paymentMethods = ["Credit Card", "Debit Card", "Payment Gateway", "Bank Transfer"];

const debounce = (func, delay) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => func(...args), delay);
  };
};

const PaymentTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
  });

  const [filters, setFilters] = useState({
    searchTerm: "",
    status: [],
    paymentMethod: [],
  });

  const [formValues, setFormValues] = useState(filters);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const contentRef = useRef();

  const handleOpenViewModal = (txn) => {
    setSelectedTransaction(txn);
    setViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setSelectedTransaction(null);
    setViewModalOpen(false);
  };

  const fetchTransactions = async (
    page = 1,
    limit = 10,
    activeFilters = filters
  ) => {
    try {
      const value = { page, limit, ...activeFilters };
      const { data } = await PaymentServices.getData(value);
      setTransactions(data?.payments);
      setPagination((prev) => ({ ...prev, totalPages: data.pages || 1 }));
    } catch (err) {
      console.error(err);
    }
  };

  const debouncedSetFilters = useMemo(
    () =>
      debounce((vals) => {
        setFilters(vals);
        setPagination((prev) => ({ ...prev, page: 1 }));
      }, 500),
    []
  );

const handleDownloadPDF = async () => {
  const input = contentRef.current;
  if (!input) return;

  const canvas = await html2canvas(input, {
    scale: 2, // better quality
    useCORS: true
  });

  const imgData = canvas.toDataURL("image/png");
  const pdf = new jsPDF("p", "mm", "a4");

  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();

  const imgProps = pdf.getImageProperties(imgData);
  const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

  let heightLeft = imgHeight;
  let position = 0;

  // First page
  pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
  heightLeft -= pdfHeight;

  // Extra pages
  while (heightLeft > 0) {
    position = heightLeft - imgHeight;
    pdf.addPage();
    pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
    heightLeft -= pdfHeight;
  }

  pdf.save(`Transaction_${selectedTransaction?.transactionId}.pdf`);
};


  useEffect(() => {
    debouncedSetFilters(formValues);
  }, [formValues]);

  useEffect(() => {
    fetchTransactions(pagination.page, pagination.limit, filters);
  }, [filters, pagination.page]);

  const handleStatusUpdate = async (id, newStatus) => {
    const isConfirm = window.confirm(
      "Are you sure you want to update the payment status?"
    );

    if (!isConfirm) return;

    try {
      await PaymentServices.update({ status: newStatus, id });
      fetchTransactions(pagination.page, pagination.limit, filters);
      alert("Payment status updated successfully.");
    } catch (err) {
      console.error("Failed to update payment status:", err);
      alert(
        "An error occurred while updating the payment status. Please try again."
      );
    }
  };

  return (
    <Box p={3}>
      <Formik initialValues={filters} enableReinitialize onSubmit={() => { }}>
        {({ values, handleChange }) => {
          setFormValues(values);

          return (
            <Grid display="flex" gap={2} mb={2}>
              <TextField
                fullWidth
                name="searchTerm"
                label="Search by Transaction ID"
                variant="outlined"
                value={values.searchTerm}
                onChange={handleChange}
              />

              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  multiple
                  name="status"
                  value={values.status}
                  onChange={handleChange}
                  renderValue={(selected) => selected.join(", ")}
                  label="Status"
                >
                  {statusOptions.map((status) => (
                    <MenuItem key={status} value={status}>
                      <Checkbox checked={values.status.indexOf(status) > -1} />
                      <ListItemText primary={status} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>


              <FormControl fullWidth>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  multiple
                  name="paymentMethod"
                  value={values.paymentMethod}
                  onChange={handleChange}
                  renderValue={(selected) => selected.join(", ")}
                  label="Payment Method"
                >
                  {paymentMethods.map((method) => (
                    <MenuItem key={method} value={method}>
                      <Checkbox
                        checked={values.paymentMethod.indexOf(method) > -1}
                      />
                      <ListItemText primary={method} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          );
        }}
      </Formik>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Transaction ID</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Payment Method</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Update Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.length > 0 ? (
              transactions.map((txn) => (
                <TableRow key={txn._id}>
                  <TableCell>{txn.transactionId}</TableCell>
                  <TableCell>{txn.userId?.name || "N/A"}</TableCell>
                  <TableCell>{txn.userId?.email || "N/A"}</TableCell>
                  <TableCell>${txn.amount.toFixed(2)}</TableCell>
                  <TableCell>{txn.productType || "NA"}</TableCell>
                  <TableCell>{txn.paymentMethod}</TableCell>
                  <TableCell>{showDateTime(txn.createdAt)}</TableCell>
                  <TableCell>
                    <Chip
                      label={txn.status}
                      color={
                        txn.status === "Transfer Complete" ? "info" :
                          txn.status === "Completed"
                            ? "success"
                            : txn.status === "Pending"
                              ? "warning"
                              : "error"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={txn.status}
                      onChange={(e) =>
                        handleStatusUpdate(txn._id, e.target.value)
                      }
                      disabled={(txn.status === "Pending" || txn.status === "Failed") ? true : false}
                    >
                      {
                        (txn.status === "Pending" || txn.status === "Failed")
                          ? statusOptions?.map((status) => (
                            <MenuItem key={status} value={status}>
                              {status}
                            </MenuItem>
                          ))
                          : statusOptionsChange?.map((status) => (
                            <MenuItem key={status} value={status}>
                              {status}
                            </MenuItem>
                          ))
                      }
                    </Select>
                  </TableCell>

                  <TableCell>
                    <IconButton onClick={() => handleOpenViewModal(txn)}>
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No record found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={2} display="flex" justifyContent="center">
        {transactions.length > 0 ? (
          <Pagination
            count={pagination.totalPages}
            page={pagination.page}
            onChange={(e, page) => setPagination((prev) => ({ ...prev, page }))}
            hidePrevButton={pagination.page === 1}
            hideNextButton={pagination.page >= pagination.totalPages}
          />
        ) : ('')}
      </Box>

      <Dialog
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Payment Details</DialogTitle>
        <DialogContent dividers>
          {selectedTransaction && (
            <Box
              display="flex"
              flexDirection="column"
              gap={2}
              ref={contentRef}
              sx={{
                backgroundColor: "#ffffff",
                color: "#333333",
                padding: 2,
                borderRadius: 2,
                fontFamily: "Arial, sans-serif",
              }}
            >
              {/* Section: Transaction Details */}
              <Typography variant="h6" fontWeight="bold">
                Transaction Details
              </Typography>
              <Divider />
              <Box pl={1} display="flex" flexDirection="column" gap={1}>
                <Typography variant="body1">
                  <strong>Transaction ID:</strong>{" "}
                  {selectedTransaction.transactionId}
                </Typography>
                <Typography variant="body1">
                  <strong>Amount:</strong> $
                  {selectedTransaction.amount.toFixed(2)}
                </Typography>
                <Typography variant="body1">
                  <strong>Payment Method:</strong>{" "}
                  {selectedTransaction.paymentMethod}
                </Typography>
                <Typography variant="body1">
                  <strong>Status:</strong>{" "}
                  <span
                    style={{
                      color:
                        selectedTransaction.status === "completed"
                          ? "green"
                          : selectedTransaction.status === "failed"
                            ? "red"
                            : "orange",
                      fontWeight: 600,
                      textTransform: "capitalize",
                    }}
                  >
                    {selectedTransaction.status}
                  </span>
                </Typography>
                <Typography variant="body1">
                  <strong>Date:</strong>{" "}
                  {new Date(selectedTransaction.createdAt).toLocaleString()}
                </Typography>
              </Box>

              {/* Section: User Details */}
              <Typography variant="h6" fontWeight="bold" mt={2}>
                User Information
              </Typography>
              <Divider />
              <Box pl={1} display="flex" flexDirection="column" gap={1}>
                <Typography variant="body1">
                  <strong>Name:</strong>{" "}
                  {selectedTransaction.userId?.name || "N/A"}
                </Typography>
                <Typography variant="body1">
                  <strong>Email:</strong>{" "}
                  {selectedTransaction.userId?.email || "N/A"}
                </Typography>
                <Typography variant="body1">
                  <strong>Address:</strong>{" "}
                  {selectedTransaction.userId?.address || "N/A"}
                </Typography>
              </Box>

              {/* Section: Product Info */}
              <Typography variant="h6" fontWeight="bold" mt={2}>
                Product Information
              </Typography>
              <Divider />
              <Box pl={1} display="flex" flexDirection="column" gap={1}>
                <Typography variant="body1">
                  <strong>Product Type:</strong>{" "}
                  {selectedTransaction.productType || "N/A"}
                </Typography>

                {selectedTransaction.productType === "ticket" ? (
                  <>
                    <Typography variant="body1">
                      <strong>Ticket Name:</strong>{" "}
                      {selectedTransaction.product?.ticketName || "N/A"}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Ticket Number:</strong>{" "}
                      {selectedTransaction.product?.ticketNumber || "N/A"}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Event Start Date</strong>{" "}
                      {new Date(selectedTransaction.ticketUnqueData?.event_start_date).toLocaleDateString() || "N/A"}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Row:</strong>{" "}
                      {selectedTransaction.ticketUnqueData?.row || "N/A"}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Section:</strong>{" "}
                      {selectedTransaction.ticketUnqueData?.section || "N/A"}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Buy Now Price:</strong> $
                      {selectedTransaction.product?.price?.toFixed(2) || "N/A"}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Bid Price:</strong> $
                      {selectedTransaction.product?.bidPrice?.toFixed(2) ||
                        "N/A"}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Auction Bid:</strong> $
                      {selectedTransaction.product?.currentBid?.toFixed(2) ||
                        "N/A"}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Highest Bidder:</strong>{" "}
                      {selectedTransaction.product?.highestBidder || "N/A"}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Auction Start:</strong>{" "}
                      {new Date(
                        selectedTransaction.product?.auctionStartDate
                      ).toLocaleString() || "N/A"}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Payment Status:</strong>{" "}
                      {selectedTransaction.product?.paymentStatus || "N/A"}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body1">
                      <strong>Product Name:</strong>{" "}
                      {selectedTransaction.product?.name || "N/A"}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Buy Now Price:</strong> $
                      {selectedTransaction.product?.price?.toFixed(2) || "N/A"}
                    </Typography>

                    {selectedTransaction.product?.description?.length > 0 && (
                      <Box mt={1}>
                        <Typography variant="body1" fontWeight="bold">
                          Product Description:
                        </Typography>
                        <ul
                          style={{ paddingLeft: "1.5rem", marginTop: "0.5rem" }}
                        >
                          {selectedTransaction.product.description.map(
                            (desc, idx) => (
                              <li key={idx}>
                                <Typography variant="body2">{desc}</Typography>
                              </li>
                            )
                          )}
                        </ul>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </Box>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleDownloadPDF()}
            sx={{ mt: 2 }}
          >
            Download PDF
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PaymentTransactions;